import React, { useState, Fragment } from 'react';
import { ArrowUpRight, ArrowDownLeft, ChevronDown, ChevronUp, Download, Filter, Wallet, Clock, CheckCircle2, AlertCircle, ArrowRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
const transactions = [{
  id: 'TX-9021',
  customer: 'Global Tech Solutions',
  date: 'Oct 14, 2023',
  retail: 1250,
  fee: 350,
  earnings: 900,
  status: 'Settled'
}, {
  id: 'TX-9020',
  customer: 'Toronto Creative Hub',
  date: 'Oct 13, 2023',
  retail: 450,
  fee: 120,
  earnings: 330,
  status: 'Processing'
}, {
  id: 'TX-9019',
  customer: 'New York FinTech',
  date: 'Oct 12, 2023',
  retail: 2100,
  fee: 550,
  earnings: 1550,
  status: 'Settled'
}, {
  id: 'TX-9018',
  customer: 'North Star Logistics',
  date: 'Oct 11, 2023',
  retail: 85,
  fee: 25,
  earnings: 60,
  status: 'Settled'
}, {
  id: 'TX-9017',
  customer: 'Vancouver Tours Ltd',
  date: 'Oct 10, 2023',
  retail: 650,
  fee: 180,
  earnings: 470,
  status: 'Failed'
}];
const payoutHistory = [{
  id: 'PO-8821',
  date: 'Oct 01, 2023',
  amount: 3200,
  method: 'Bank Transfer • **** 4291',
  status: 'Settled'
}, {
  id: 'PO-8820',
  date: 'Sep 15, 2023',
  amount: 2850,
  method: 'Bank Transfer • **** 4291',
  status: 'Settled'
}, {
  id: 'PO-8819',
  date: 'Sep 01, 2023',
  amount: 4100,
  method: 'Bank Transfer • **** 4291',
  status: 'Settled'
}];
const availableBalance = transactions.reduce((acc, tx) => acc + tx.earnings, 0);
const pendingClear = 5000; // Static value or computed separately if required
export function Transactions() {
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const [payoutFreq, setPayoutFreq] = useState('Weekly');
  return <div className="space-y-6 max-w-[1600px] mx-auto" data-id="element-2130">
      <div className="flex items-center justify-between bg-white p-6 rounded-lg border border-gray-200 shadow-sm" data-id="element-2131">
        <div data-id="element-2132">
          <h1 className="text-2xl font-bold text-slate-900" data-id="element-2133">
            Transparency Hub
          </h1>
          <p className="text-sm text-gray-500 font-medium" data-id="element-2134">
            Money In: Real-time profit splits and automated payout tracking.
          </p>
        </div>
        <div className="flex gap-3" data-id="element-2135">
          <button className="px-4 py-2.5 bg-white border border-gray-200 rounded-lg text-sm font-bold text-gray-600 hover:bg-gray-50 transition-all flex items-center gap-2 shadow-sm" data-id="element-2136">
            <Download size={18} data-id="element-2137" />
            Export Ledger
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-2138">
        {/* Payout Request Module */}
        <div className="bg-slate-900 p-8 rounded-lg text-white space-y-8 relative overflow-hidden" data-id="element-2139">
          <div className="relative z-10" data-id="element-2140">
            <div className="flex items-center justify-between mb-8" data-id="element-2141">
              <div className="p-3 bg-white/10 rounded-lg" data-id="element-2142">
                <Wallet size={24} className="text-indigo-400" data-id="element-2143" />
              </div>
              <div className="flex bg-white/5 p-1 rounded-lg border border-white/10" data-id="element-2144">
                {['Daily', 'Weekly', 'Monthly'].map(f => <button key={f} onClick={() => setPayoutFreq(f)} className={`px-3 py-1 text-[10px] font-bold rounded-md transition-all ${payoutFreq === f ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-400 hover:text-white'}`} data-id="element-2145">
                    {f}
                  </button>)}
              </div>
            </div>

            <div className="space-y-1" data-id="element-2146">
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest" data-id="element-2147">
                Available for Payout
              </p>
              <h2 className="text-4xl font-bold" data-id="element-2148">
                ${availableBalance.toLocaleString()}
              </h2>
            </div>

            <div className="mt-8 space-y-4" data-id="element-2149">
              <button className="w-full py-3 bg-indigo-600 text-white rounded-lg text-sm font-bold shadow-lg shadow-indigo-500/20 hover:bg-indigo-700 transition-all" data-id="element-2150">
                Request Payout
              </button>
              <div className="space-y-2" data-id="element-2151">
                <div className="flex justify-between text-[10px] font-bold uppercase tracking-widest" data-id="element-2152">
                  <span className="text-slate-400" data-id="element-2153">Funds Clearing</span>
                  <span className="text-indigo-400" data-id="element-2154">75% Complete</span>
                </div>
                <div className="w-full bg-white/10 h-1.5 rounded-full overflow-hidden" data-id="element-2155">
                  <div className="h-full bg-indigo-500 w-3/4" data-id="element-2156"></div>
                </div>
                <p className="text-[10px] text-slate-500 font-medium" data-id="element-2157">
                  Next clearing cycle in 3 days.
                </p>
              </div>
            </div>
          </div>
          <div className="absolute -right-8 -bottom-8 opacity-5" data-id="element-2158">
            <Wallet size={200} data-id="element-2159" />
          </div>
        </div>

        {/* Transactions Table */}
        <div className="lg:col-span-2 bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden flex flex-col" data-id="element-2160">
          <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-gray-50/50" data-id="element-2161">
            <h3 className="font-bold text-slate-900 text-sm" data-id="element-2162">
              Profit Split Ledger
            </h3>
            <div className="flex gap-2" data-id="element-2163">
              <button className="p-2 text-gray-400 hover:text-gray-600 transition-colors" data-id="element-2164">
                <Filter size={18} data-id="element-2165" />
              </button>
            </div>
          </div>
          <div className="flex-1 overflow-x-auto" data-id="element-2166">
            <table className="w-full text-left border-collapse" data-id="element-2167">
              <thead data-id="element-2168">
                <tr className="text-[10px] font-bold uppercase tracking-wider text-gray-400 border-b border-gray-100" data-id="element-2169">
                  <th className="px-6 py-4" data-id="element-2170">Transaction</th>
                  <th className="px-6 py-4" data-id="element-2171">Customer</th>
                  <th className="px-6 py-4" data-id="element-2172">Profit Split</th>
                  <th className="px-6 py-4" data-id="element-2173">Status</th>
                  <th className="px-6 py-4 w-10" data-id="element-2174"></th>
                </tr>
              </thead>
              <tbody className="text-sm" data-id="element-2175">
                {transactions.map(tx => <Fragment key={tx.id} data-id="element-2176">
                    <tr onClick={() => setExpandedRow(expandedRow === tx.id ? null : tx.id)} className={`hover:bg-gray-50 cursor-pointer transition-colors group ${expandedRow === tx.id ? 'bg-indigo-50/30' : ''}`} data-id="element-2177">
                      <td className="px-6 py-4" data-id="element-2178">
                        <p className="font-bold text-slate-900" data-id="element-2179">{tx.id}</p>
                        <p className="text-[10px] font-bold text-gray-400 uppercase" data-id="element-2180">
                          {tx.date}
                        </p>
                      </td>
                      <td className="px-6 py-4 font-bold text-slate-700" data-id="element-2181">
                        {tx.customer}
                      </td>
                      <td className="px-6 py-4" data-id="element-2182">
                        <div className="flex items-center gap-2" data-id="element-2183">
                          <span className="text-gray-400 font-medium" data-id="element-2184">
                            ${tx.retail.toLocaleString()}
                          </span>
                          <span className="text-gray-300 text-[10px] font-bold" data-id="element-2185">
                            RETAIL
                          </span>
                          <ArrowRight size={12} className="text-gray-300" data-id="element-2186" />
                          <span className="text-red-400 font-medium" data-id="element-2187">
                            -${tx.fee.toLocaleString()}
                          </span>
                          <span className="text-gray-300 text-[10px] font-bold" data-id="element-2188">
                            FEE
                          </span>
                          <ArrowRight size={12} className="text-gray-300" data-id="element-2189" />
                          <span className="text-indigo-600 font-bold" data-id="element-2190">
                            ${tx.earnings.toLocaleString()}
                          </span>
                          <span className="text-indigo-300 text-[10px] font-bold uppercase" data-id="element-2191">
                            Profit
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4" data-id="element-2192">
                        <span className={`px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${tx.status === 'Settled' ? 'bg-emerald-50 text-emerald-600' : tx.status === 'Processing' ? 'bg-amber-50 text-amber-600' : 'bg-red-50 text-red-600'}`} data-id="element-2193">
                          {tx.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-gray-300 group-hover:text-indigo-400 transition-colors" data-id="element-2194">
                        {expandedRow === tx.id ? <ChevronUp size={18} data-id="element-2195" /> : <ChevronDown size={18} data-id="element-2196" />}
                      </td>
                    </tr>
                    <AnimatePresence data-id="element-2197">
                      {expandedRow === tx.id && <tr data-id="element-2198">
                          <td colSpan={5} className="p-0" data-id="element-2199">
                            <motion.div initial={{
                        height: 0,
                        opacity: 0
                      }} animate={{
                        height: 'auto',
                        opacity: 1
                      }} exit={{
                        height: 0,
                        opacity: 0
                      }} className="overflow-hidden bg-gray-50/50 border-b border-gray-100" data-id="element-2200">
                              <div className="px-12 py-6 grid grid-cols-3 gap-8" data-id="element-2201">
                                <div className="space-y-1" data-id="element-2202">
                                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest" data-id="element-2203">
                                    Retail Price
                                  </p>
                                  <p className="text-lg font-bold text-slate-900" data-id="element-2204">
                                    ${tx.retail.toLocaleString()}.00
                                  </p>
                                  <p className="text-[10px] text-gray-500 font-medium" data-id="element-2205">
                                    Paid via Stripe Canada
                                  </p>
                                </div>
                                <div className="space-y-1" data-id="element-2206">
                                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest" data-id="element-2207">
                                    NiishEngine Fee
                                  </p>
                                  <p className="text-lg font-bold text-red-500" data-id="element-2208">
                                    -${tx.fee.toLocaleString()}.00
                                  </p>
                                  <p className="text-[10px] text-gray-500 font-medium" data-id="element-2209">
                                    Infrastructure & Fulfillment
                                  </p>
                                </div>
                                <div className="space-y-1" data-id="element-2210">
                                  <p className="text-[10px] font-bold text-indigo-600 uppercase tracking-widest" data-id="element-2211">
                                    Your Net Profit
                                  </p>
                                  <p className="text-lg font-bold text-indigo-600" data-id="element-2212">
                                    ${tx.earnings.toLocaleString()}.00
                                  </p>
                                  <p className="text-[10px] text-indigo-400 font-medium" data-id="element-2213">
                                    Available in 48 hours
                                  </p>
                                </div>
                              </div>
                            </motion.div>
                          </td>
                        </tr>}
                    </AnimatePresence>
                  </Fragment>)}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Payout History */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden flex flex-col" data-id="element-2214">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-gray-50/50" data-id="element-2215">
          <h3 className="font-bold text-slate-900 text-sm" data-id="element-2216">Payout History</h3>
          <button className="text-xs font-bold text-indigo-600 hover:underline" data-id="element-2217">
            View All Payouts
          </button>
        </div>
        <div className="overflow-x-auto" data-id="element-2218">
          <table className="w-full text-left border-collapse" data-id="element-2219">
            <thead data-id="element-2220">
              <tr className="text-[10px] font-bold uppercase tracking-wider text-gray-400 border-b border-gray-100" data-id="element-2221">
                <th className="px-6 py-4" data-id="element-2222">Payout ID</th>
                <th className="px-6 py-4" data-id="element-2223">Date</th>
                <th className="px-6 py-4" data-id="element-2224">Amount</th>
                <th className="px-6 py-4" data-id="element-2225">Method</th>
                <th className="px-6 py-4" data-id="element-2226">Status</th>
              </tr>
            </thead>
            <tbody className="text-sm" data-id="element-2227">
              {payoutHistory.map(po => <tr key={po.id} className="hover:bg-gray-50 transition-colors" data-id="element-2228">
                  <td className="px-6 py-4 font-bold text-slate-900" data-id="element-2229">
                    {po.id}
                  </td>
                  <td className="px-6 py-4 font-medium text-gray-500" data-id="element-2230">
                    {po.date}
                  </td>
                  <td className="px-6 py-4 font-bold text-slate-900" data-id="element-2231">
                    ${po.amount.toLocaleString()}.00
                  </td>
                  <td className="px-6 py-4 text-gray-500 font-medium" data-id="element-2232">
                    {po.method}
                  </td>
                  <td className="px-6 py-4" data-id="element-2233">
                    <span className={`px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${po.status === 'Settled' ? 'bg-emerald-50 text-emerald-600' : po.status === 'Processing' ? 'bg-amber-50 text-amber-600' : 'bg-red-50 text-red-600'}`} data-id="element-2234">
                      {po.status}
                    </span>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </div>
    </div>;
}